import { createSelector } from '@reduxjs/toolkit'
import { RootState } from './index'
import { Folder } from '@/types'
import { ACTIVE_STATUSES } from './slices/uploadsSlice'

// Base selectors
export const selectFoldersState = (state: RootState) => state.folders
export const selectFilesState = (state: RootState) => state.files
export const selectUIState = (state: RootState) => state.ui

// Folder selectors
export const selectFolderTree = createSelector(
  [selectFoldersState],
  (foldersState) => foldersState.tree
)

export const selectFoldersById = createSelector(
  [selectFoldersState],
  (foldersState) => foldersState.byId
)

export const selectFoldersLoading = createSelector(
  [selectFoldersState],
  (foldersState) => foldersState.isLoading
)

export const selectFoldersError = createSelector(
  [selectFoldersState],
  (foldersState) => foldersState.error
)

export const selectFoldersInitialized = createSelector(
  [selectFoldersState],
  (foldersState) => foldersState.initialized
)

export const selectFolderById = createSelector(
  [selectFoldersById, (state: RootState, folderId: number) => folderId],
  (foldersById, folderId) => foldersById[folderId] || null
)

// Create folder map for O(1) lookups - properly memoized
export const selectFolderMap = createSelector([selectFolderTree], (tree) => {
  const map = new Map<number, Folder>()

  const addToMap = (folder: Folder) => {
    map.set(folder.id, folder)
    const children = folder.subfolders || folder.children || []
    children.forEach(addToMap)
  }

  tree.forEach(addToMap)
  return map
})

// Create parent map for navigation - properly memoized
export const selectParentMap = createSelector([selectFolderTree], (tree) => {
  const parentMap = new Map<number, Folder | null>()

  const addParentRelations = (folder: Folder, parent: Folder | null = null) => {
    parentMap.set(folder.id, parent)
    const children = folder.subfolders || folder.children || []
    children.forEach((child) => addParentRelations(child, folder))
  }

  tree.forEach((folder) => addParentRelations(folder))
  return parentMap
})

// Get parent folder of a specific folder
export const selectParentFolder = createSelector(
  [selectParentMap, (state: RootState, folderId: number) => folderId],
  (parentMap, folderId) => parentMap.get(folderId) || null
)

// Get all ancestors of a folder (for breadcrumb)
export const selectFolderAncestors = createSelector(
  [selectParentMap, (state: RootState, folderId: number) => folderId],
  (parentMap, folderId) => {
    const ancestors: Folder[] = []
    let currentId = folderId

    while (currentId) {
      const parent = parentMap.get(currentId)
      if (parent) {
        ancestors.unshift(parent) // Add to beginning
        currentId = parent.id
      } else {
        break
      }
    }

    return ancestors
  }
)

// File selectors
export const selectFilesByFolder = createSelector(
  [selectFilesState, (state: RootState, folderId: number | string) => folderId],
  (filesState, folderId) => filesState.byFolderId[folderId] || []
)

export const selectFilesById = createSelector([selectFilesState], (filesState) => filesState.byId)

export const selectFileById = createSelector(
  [selectFilesById, (state: RootState, fileId: number) => fileId],
  (filesById, fileId) => filesById[fileId] || null
)

export const selectFilesLoading = createSelector(
  [selectFilesState, (state: RootState, folderId: number | string) => folderId],
  (filesState, folderId) => filesState.isLoading[folderId] || false
)

export const selectFilesError = createSelector([selectFilesState], (filesState) => filesState.error)

export const selectRootFiles = createSelector(
  [selectFilesState],
  (filesState) => filesState.byFolderId.root || []
)

// UI selectors
export const selectSelectedFile = createSelector([selectUIState], (uiState) => uiState.selectedFile)

export const selectLastSelectedFileId = createSelector(
  [selectUIState],
  (uiState) => uiState.lastSelectedFileId
)

export const selectSelectedFolder = createSelector(
  [selectUIState],
  (uiState) => uiState.selectedFolder
)

// Get the current selected folder with updated data from folders state
export const selectCurrentSelectedFolder = createSelector(
  [selectSelectedFolder, selectFolderMap],
  (selectedFolder, folderMap) => {
    if (!selectedFolder) return null
    // Always return the fresh folder data from folders state
    return folderMap.get(selectedFolder.id) || selectedFolder
  }
)

// Memoized Set selectors that only create new instances when data changes
export const selectSelectedFiles = createSelector([selectUIState], (uiState) => {
  // Create a cached Set - this will only change when the array reference changes
  const cachedSet = new Set(uiState.selectedFiles)
  return cachedSet
})

export const selectExpandedFolders = createSelector([selectUIState], (uiState) => {
  // Create a cached Set - this will only change when the array reference changes
  const cachedSet = new Set(uiState.expandedFolders)
  return cachedSet
})

export const selectNavigationHistory = createSelector(
  [selectUIState],
  (uiState) => uiState.navigationHistory
)

// Modal selectors
export const selectCreateFolderModalOpen = createSelector(
  [selectUIState],
  (uiState) => uiState.isCreateFolderModalOpen
)

export const selectEditFolderModalOpen = createSelector(
  [selectUIState],
  (uiState) => uiState.isEditFolderModalOpen
)

export const selectDeleteFolderModalOpen = createSelector(
  [selectUIState],
  (uiState) => uiState.isDeleteFolderModalOpen
)

export const selectUploadModalOpen = createSelector(
  [selectUIState],
  (uiState) => uiState.isUploadModalOpen
)

export const selectFolderToEdit = createSelector([selectUIState], (uiState) => uiState.folderToEdit)

export const selectFolderToDelete = createSelector(
  [selectUIState],
  (uiState) => uiState.folderToDelete
)

// Combined selectors
export const selectCurrentFolderFiles = createSelector(
  [selectCurrentSelectedFolder, selectRootFiles, selectFilesState],
  (selectedFolder, rootFiles, filesState) => {
    if (!selectedFolder) {
      return rootFiles
    }
    return filesState.byFolderId[selectedFolder.id] || []
  }
)

// Get files for selected files IDs
export const selectSelectedFileItems = createSelector(
  [selectSelectedFiles, selectFilesById],
  (selectedFileIds, filesById) => {
    return Array.from(selectedFileIds)
      .map((id) => filesById[id])
      .filter(Boolean)
  }
)

// Get all files count
export const selectTotalFilesCount = createSelector([selectFilesState], (filesState) => {
  return Object.keys(filesState.byId).length
})

// Get files count for a specific folder
export const selectFolderFilesCount = createSelector(
  [selectFilesState, (state: RootState, folderId: number | string) => folderId],
  (filesState, folderId) => {
    return filesState.byFolderId[folderId]?.length || 0
  }
)

// Check if a folder is expanded
export const selectIsFolderExpanded = createSelector(
  [selectExpandedFolders, (state: RootState, folderId: number) => folderId],
  (expandedFolders, folderId) => expandedFolders.has(folderId)
)

// Check if any files are selected
export const selectHasSelectedFiles = createSelector(
  [selectSelectedFiles],
  (selectedFiles) => selectedFiles.size > 0
)

// Combined navigation state selector to prevent object recreation in useNavigation hook
export const selectNavigationState = createSelector(
  [
    selectFolderTree,
    selectFoldersLoading,
    selectFoldersError,
    selectSelectedFile,
    selectCurrentSelectedFolder,
    selectSelectedFiles,
    selectLastSelectedFileId,
    selectExpandedFolders,
    selectNavigationHistory,
    selectRootFiles,
    selectFilesError,
    selectCurrentFolderFiles,
  ],
  (
    folderTree,
    isLoadingTree,
    treeError,
    selectedFile,
    selectedFolder,
    selectedFiles,
    lastSelectedFileId,
    expandedFolders,
    navigationHistory,
    rootFiles,
    filesError,
    folderFiles
  ) => ({
    folderTree,
    isLoadingTree,
    treeError,
    selectedFile,
    selectedFolder,
    selectedFiles,
    lastSelectedFileId,
    expandedFolders,
    navigationHistory,
    rootFiles,
    filesError,
    folderFiles,
  })
)

// ---------------------------------------------------------------------------
// Uploads
// ---------------------------------------------------------------------------

export const selectUploadsState = (state: RootState) => state.uploads

export const selectUploadItems = createSelector(
  [selectUploadsState],
  (uploads) => uploads.items
)

export const selectUploadPanelMinimized = createSelector(
  [selectUploadsState],
  (uploads) => uploads.isMinimized
)

export const selectPendingFolderNavigation = createSelector(
  [selectUploadsState],
  (uploads) => uploads.pendingFolderNavigation
)

export const selectHasActiveUploads = createSelector([selectUploadItems], (items) =>
  items.some((item) => ACTIVE_STATUSES.includes(item.status))
)

/**
 * The panel is on screen whenever it is open and still has something to show.
 * The chat widget hides while this is true — they share the same corner.
 */
export const selectIsUploadPanelVisible = createSelector(
  [selectUploadsState],
  (uploads) => uploads.isPanelOpen && uploads.items.length > 0
)

export const selectUploadSummary = createSelector([selectUploadItems], (items) => {
  const active = items.filter((item) => ACTIVE_STATUSES.includes(item.status))
  const completed = items.filter((item) => item.status === 'completed')
  const failed = items.filter((item) => item.status === 'failed')
  const skipped = items.filter((item) => item.status === 'skipped')
  const conflicted = items.filter((item) => item.status === 'conflict')

  // Weight by bytes so one large file does not jump to 100% behind a small one.
  const totalBytes = items.reduce((total, item) => total + (item.size || 0), 0)
  const doneBytes = items.reduce(
    (total, item) => total + (item.size || 0) * (item.progress / 100),
    0
  )

  return {
    total: items.length,
    activeCount: active.length,
    completedCount: completed.length,
    failedCount: failed.length,
    skippedCount: skipped.length,
    conflictCount: conflicted.length,
    isActive: active.length > 0,
    progress: totalBytes > 0 ? Math.round((doneBytes / totalBytes) * 100) : 0,
  }
})
